import { useEffect, useRef } from "react";
import ReactEcharts, { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { formatMinutes } from "../../../../app/datetimeUtils";
import { tooltipMark, miwChartColors, utahChartColors } from "../../shared/reportUtil";

// Required properties for the chart
interface LessonUsageRequiredBase {
	totalMinutes: number;
}

interface LessonUsageBarChartProps<T extends LessonUsageRequiredBase> {
	data: T[];
	hasData: boolean;
	title: string;
	yAxisDataField: keyof T; // Property name to use for y-axis labels
	seriesName: string;
	yAxisLabelWidth?: number;
}

export function LessonUsageBarChart<T extends LessonUsageRequiredBase>(
	props: LessonUsageBarChartProps<T>
) {
	const chartRef = useRef<ReactEcharts>(null);
	const { config } = useAppSelector((state) => state.site);
	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;
	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const initialEChartOptions: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		title: {
			text: props.title,
			left: "center",
			textStyle: { fontSize: 16, fontWeight: 600, color: foregroundColor },
		},
		grid: {
			containLabel: true,
			left: "3%",
			right: 70,
			bottom: 30,
			top: 40,
		},
		xAxis: {
			type: "value",
			show: true,
			name: props.seriesName,
			nameLocation: "middle",
			nameGap: 30,
			min: 0,
			minInterval: 1,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
					width: 1,
				},
			},
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
			axisLabel: {
				formatter: (value: number) => formatMinutes(value),
			},
		},
		yAxis: {
			type: "category",
			data: props.data.map((row) => row[props.yAxisDataField]),
			inverse: true, // Forces the order of the rows in the table on initial load
			axisPointer: { type: "shadow" },
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			axisLabel: {
				padding: 10,
				lineHeight: 15,
				interval: 0,
				align: "right",
				width: props.yAxisLabelWidth ?? 180,
				overflow: "break",
				hideOverlap: false,
			},
		},
		tooltip: {
			trigger: "axis",
			formatter: (
				params: {
					axisValue: string;
					dataIndex: number;
					color: string;
					seriesName: string;
					value: string;
				}[]
			) => {
				if (!params.find((p) => p.value)) return;

				const label = `<div>${props.data[params[0].dataIndex][props.yAxisDataField]}</div>`;
				const lineItem = (p: any) => {
					const formattedValue = formatMinutes(p.value);
					return `<div>${tooltipMark(p.color)} ${p.seriesName}: <b>${formattedValue}</b></div>`;
				};
				const content = params.reduce((a: string, v: any) => a + lineItem(v), label);

				return content;
			},
			textStyle: {
				fontSize: 12,
			},
		},
		animationDurationUpdate: 500,
		series: [],
	};

	useEffect(() => {
		const options = updateOptions(
			initialEChartOptions,
			buildSeriesOptions(props.data, props.seriesName, foregroundColor, chartColors)
		);

		if (chartPalette != undefined) {
			options.color = chartPalette;
		}

		chartRef.current?.getEchartsInstance().setOption(options, true);
	}, [props.data, chartPalette, foregroundColor, accentColor]);

	const chartHeight = props.data.length * 35 + 100;

	return (
		<>
			{props.hasData && (
				<div className="relative w-full mt-4 p-4 border-2 border-gray-200">
					<ReactEcharts
						ref={chartRef}
						option={initialEChartOptions}
						className="w-full !min-h-96"
						style={{
							height: chartHeight,
						}}
					/>
				</div>
			)}
		</>
	);
}

function buildSeriesOptions<T extends LessonUsageRequiredBase>(
	data: T[],
	seriesName: string,
	foregroundColor: string,
	chartColors: string[]
): EChartsOption["series"] {
	return [
		{
			name: seriesName,
			type: "bar",
			cursor: "default",
			data: data.map((row) => row.totalMinutes),
			barMinWidth: 15,
			itemStyle: {
				color: function (param: { dataIndex: number }) {
					return chartColors[param.dataIndex % chartColors.length];
				},
			},
			emphasis: {
				itemStyle: {
					shadowBlur: 7,
				},
			},
			label: {
				show: true,
				position: "right",
				fontWeight: "bold",
				color: foregroundColor,
				formatter: (params: any) => {
					return formatMinutes(params.value);
				},
			},
		},
	];
}

function updateOptions(options: EChartsOption, seriesData: EChartsOption["series"]): EChartsOption {
	options.series = seriesData;
	return options;
}
